import { useLazyQuery } from '@apollo/client';
import getConfig from 'next/config';
import { useCallback, useEffect } from 'react';

import { useApollo } from './ApolloProvider';
import { useSession } from './session/SessionContext';
import modules from '../../modules/modules';
import { NavigationItemType } from '../../types/module';
import { FETCH_MODULES, FetchModulesType } from '../api/module';
import useCustomRouter from '../hooks/useCustomRouter';
import usePreviousRoute from '../hooks/usePreviousRoute';
import { PublicRuntimeConfigType } from '../types/publicRuntimeConfig';

const AccessGuard = ({ children }: { children: JSX.Element }) => {
	const { publicRuntimeConfig } = getConfig() as PublicRuntimeConfigType;
	const { standaloneModule, isBrochureHidden } = publicRuntimeConfig;
	const { changeCustomerFile, checkPermission, customerFile: sessionCustomerFile } = useSession();
	const { id: sessionCustomerFileId } = sessionCustomerFile || {};
	const { pathname: currentPath, query, push } = useCustomRouter();
	const isSettingsDialogOpen = 'settings' in query;
	const { refetchActiveQueries } = useApollo();

	const prevPath = usePreviousRoute();

	const [fetchModules] = useLazyQuery<FetchModulesType>(FETCH_MODULES, {
		onCompleted: ({ modules: fetchedModules }) => {
			const moduleRoute = currentPath.split('/').filter(Boolean)[0] || standaloneModule;

			if (
				currentPath === '/' ||
				(modules.some(({ route }) => route.includes(moduleRoute)) &&
					fetchedModules.some(({ code }) => moduleRoute === code))
			) {
				refetchActiveQueries().catch((error) => {
					throw error;
				});
			} else {
				if (standaloneModule) {
					changeCustomerFile(undefined);
				}

				const { dossier: queryCustomerFileCode, ...restQuery } = query || {};

				push({
					pathname: '/',
					query: restQuery,
				}).catch((error) => {
					throw error;
				});
			}
		},
	});

	const getNavigationItemByPath = useCallback(
		({
			pathSegments,
			levels,
		}: {
			pathSegments: string[];
			levels?: NavigationItemType[];
		}): NavigationItemType | undefined => {
			if (pathSegments.length === 0) return undefined;

			const [currentSegment, ...remainingSegments] = pathSegments;
			const currentLevel = (levels || modules)?.find(({ route }) => route.slice(1) === currentSegment);

			if (remainingSegments.length === 0) return currentLevel;

			return getNavigationItemByPath({
				pathSegments: remainingSegments,
				levels: currentLevel?.subItems,
			});
		},
		[],
	);

	const validatePermissions = useCallback(
		({ pathSegments, level }: { pathSegments: string[]; level?: NavigationItemType }): boolean => {
			if (
				pathSegments.length === 0 ||
				(pathSegments.length !== 0 &&
					(!level?.subItems ||
						!level?.subItems.some(({ route }) => route.slice(1) === pathSegments[pathSegments.length - 1])))
			)
				return true;

			const [currentSegment, ...remainingSegments] = pathSegments;
			const currentLevel = (level?.subItems || modules)?.find(({ route }) => route.slice(1) === currentSegment);

			if (!currentLevel) return false;

			const hasPermission = currentLevel.permissionCodes?.some(checkPermission) ?? true;

			if (!hasPermission) return false;

			return validatePermissions({
				pathSegments: remainingSegments,
				level: currentLevel,
			});
		},
		[checkPermission],
	);

	useEffect(() => {
		const previousSegments = prevPath?.split('/').filter(Boolean) ?? [];
		const currentSegments = currentPath.split('/').filter(Boolean);
		const [moduleRoute] = currentSegments;

		const diffIndex = currentSegments.findIndex((segment, index) => segment !== previousSegments[index]);

		if (diffIndex === -1) return;

		const initialModule = getNavigationItemByPath({
			pathSegments: currentSegments.slice(0, diffIndex),
			levels: modules,
		});

		if (
			(isBrochureHidden === 'true' && moduleRoute.includes('brochure')) ||
			(!!standaloneModule && moduleRoute !== standaloneModule) ||
			(currentSegments.length > 0 &&
				!validatePermissions({
					pathSegments: currentSegments.slice(diffIndex),
					level: initialModule,
				}))
		) {
			push('/error/403').catch((error) => {
				throw error;
			});
		}
	}, [currentPath, getNavigationItemByPath, isBrochureHidden, prevPath, push, standaloneModule, validatePermissions]);

	useEffect(() => {
		if (sessionCustomerFileId && !isSettingsDialogOpen) {
			fetchModules().catch((error) => {
				throw error;
			});
		}
	}, [fetchModules, isSettingsDialogOpen, sessionCustomerFileId]);

	return children;
};

export default AccessGuard;
